import {
  defineScenario,
  act,
  beat,
  gp,
  infra,
  at,
  PACING,
  establishingWide,
  dronePushIn,
  orbit180,
  craneReveal,
  majesticPullback,
  hudEvent,
  hudTeam,
  notification,
  productScreen,
  panel,
  updatePanel,
  removePanel,
  spawnVehicle,
  dimStart,
  dimEnd,
  hold,
} from '../authoring';
import type { ScenarioStep } from '../../core/types';

// ──────────────────────────────────────────────────────────────────────────
// SCENARIO — ENERGY TRANSITION (NET-ZERO TANKER ROUTE OPTIMISATION)
//
// CSO overflow triggers tankering dispatch. AI optimises routes to minimise
// carbon while meeting SLA. The story is about ROUTES and CARBON.
//
// UNIQUE VISUAL DNA:
// 1. flow-pulse as TANKER ROUTES — the visual is NETWORK LINES not points
// 2. TWO routes shown simultaneously: old (red, longer) vs AI-optimised
//    (green, shorter) — a ROUTE COMPARISON effect
// 3. Carbon counter hud-metric as the PRIMARY KPI (not budget/pressure)
// 4. Colour grade shifts: industrial amber → clean green
// 5. Vehicles are SECONDARY — the route line itself is the star
// 6. CSO overflow point as the trigger (geyser-like visual at start)
// 7. Multiple flow-pulse paths shown simultaneously
//
// No excavation, no pipe-lay, no comms-broadcast. Routes + Carbon.
// ──────────────────────────────────────────────────────────────────────────

// ─── Sites ─────────────────────────────────────────────────────────────────
const CSO = infra('cso-river', gp(4, -24));
const TREATMENT = infra('water-treatment', gp(-22, -22));
const DEPOT = infra('depot-west', gp(-26, 6));

// Old route (longer, more carbon) — goes via the north
const OLD_ROUTE: [number, number][] = [
  gp(-26, 6), gp(-20, 4), gp(-14, 0), gp(-8, -4),
  gp(-2, -8), gp(2, -14), gp(4, -20), gp(4, -24),
];

// AI-optimised route (shorter, less carbon) — direct south-east
const NEW_ROUTE: [number, number][] = [
  gp(-26, 6), gp(-22, 0), gp(-16, -8), gp(-8, -16),
  gp(-2, -20), gp(4, -24),
];

// Return route to treatment works
const RETURN_ROUTE: [number, number][] = [
  gp(4, -24), gp(-4, -24), gp(-12, -23), gp(-18, -22), gp(-22, -22),
];

export const energyTransitionScenario = defineScenario(
  {
    id: 'energy-transition',
    title: 'Minimising Carbon Footprint – Optimised Tankering & Grab Management',
    description: 'CSO overflow triggers tankering. AI optimises routes — old vs new comparison. Carbon saved.',
    hudLabel: 'NET-ZERO OPS',
    telemetry: 'energy-transition',
    activeInfraIds: ['cso-river', 'water-treatment', 'depot-west'],
  },
  [
    // ═══════════════════════════════════════════════════════════════════════
    // ACT 1: CSO OVERFLOW — The trigger event (0–20s)
    // Dawn. Industrial amber colour grade. CSO point activates.
    // ═══════════════════════════════════════════════════════════════════════
    act('CSO Overflow', [
      dimStart,
      { type: 'color-grade', duration: 0, params: { tint: '#f59e0b', intensity: 0.08, vignette: 0.2 } } as ScenarioStep,

      beat(PACING.ESTABLISH, [
        establishingWide(CSO, { bearing: -30, pitch: 42, zoom: 14.5 }),
        { type: 'model-status', duration: 0, params: { coordinates: gp(4, -24), infraId: 'cso-river', color: '#ef4444', emissive: 0.7 } },
        hudEvent('alert', 'CSO OVERFLOW DETECTED', 'Combined sewer overflow at River Point. Level: 87%. Tankering dispatch required.', 'critical'),
        { type: 'hud-metric', duration: 0, params: { id: 'cso-level', kind: 'counter', label: 'CSO Level', value: 87, unit: '%', severity: 'critical' } },
        { type: 'hud-metric', duration: 0, params: { id: 'carbon', kind: 'counter', label: 'Carbon', value: 0, unit: 'kg CO₂', severity: 'info' } },
      ]),

      // Geyser effect at CSO
      beat(PACING.PUSH_IN, [
        dronePushIn(CSO, { bearing: 10, zoom: 16 }),
        { type: 'geyser', duration: PACING.PUSH_IN, params: { coordinates: gp(4, -24), infraId: 'cso-river', id: 'cso-overflow', intensity: 0.6, floodRadius: 30 } },
        hudEvent('dispatch', 'TANKERING REQUIRED', 'Dispatch to reduce CSO level below 60%. Standard route: 12.4km. AI optimising...', 'warning'),
        notification('CSO ALERT', 'Overflow at 87%. Tanker dispatch initiated from Depot West.', 'critical', PACING.BEAT),
      ]),

      productScreen('Wastewater Workbank — CSO', {
        component: 'placeholder',
        device: 'desktop',
        subtitle: 'Placeholder — wastewater workbank with CSO overflow alert TBD',
        role: 'Wastewater Controller',
        url: 'app.skewb.io/wastewater/workbank',
      }),
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // ACT 2: ROUTE COMPARISON — Old vs AI-optimised (20–60s)
    // The centrepiece: two flow-pulse routes shown simultaneously.
    // Red route (old, 12.4km) fading while green (new, 8.1km) brightens.
    // Carbon counter shows the saving in real-time.
    // ═══════════════════════════════════════════════════════════════════════
    act('Route Comparison', [
      // Show OLD route first (red)
      beat(8000, [
        craneReveal(DEPOT, { zoom: 13.8, bearing: -20, duration: 8000 }),
        { type: 'flow-pulse', duration: 8000, params: {
          id: 'old-route', path: OLD_ROUTE, color: '#ef4444', speed: 0.6,
        }},
        hudEvent('route', 'STANDARD ROUTE', 'Traditional route: 12.4km via North ring. Est. carbon: 18.6kg CO₂.', 'warning'),
        { type: 'hud-metric', duration: 0, params: { id: 'distance', kind: 'counter', label: 'Route Distance', value: 12400, unit: 'm', severity: 'warning' } },
        panel('route-compare', 'Route Comparison', 'data', DEPOT, [
          { label: 'Standard Route', value: '12.4km', status: 'critical' },
          { label: 'Carbon (Standard)', value: '18.6kg CO₂', status: 'critical' },
          { label: 'AI Route', value: 'Calculating...', status: 'warning' },
          { label: 'Carbon (AI)', value: 'Calculating...', status: 'warning' },
        ]),
      ]),

      // Show NEW route alongside (green) — both visible simultaneously
      beat(8000, [
        orbit180(at(-10, -10), { startBearing: -20, arc: 90, zoom: 13.5, pitch: 42, duration: 8000 }),
        { type: 'flow-pulse', duration: 8000, params: {
          id: 'new-route', path: NEW_ROUTE, color: '#10b981', speed: 1.2,
        }},
        hudEvent('ai', 'AI-OPTIMISED ROUTE', 'Optimised route: 8.1km direct south-east. Carbon: 12.1kg CO₂. Saving: 35%.', 'success'),
        updatePanel('route-compare', [
          { label: 'Standard Route', value: '12.4km (red)', status: 'critical' },
          { label: 'Carbon (Standard)', value: '18.6kg CO₂', status: 'critical' },
          { label: 'AI Route', value: '8.1km (green)', status: 'good' },
          { label: 'Carbon (AI)', value: '12.1kg CO₂', status: 'good' },
        ]),
        notification('35% CARBON REDUCTION', 'AI route saves 6.5kg CO₂ per trip. 4.3km shorter. Same SLA met.', 'success', 5000),
      ]),

      // Old route fades, new route stays
      { type: 'clear-effect', duration: 0, params: { id: 'old-route' } } as ScenarioStep,

      beat(PACING.BEAT, [
        { type: 'camera', duration: PACING.BEAT, params: { center: gp(-10, -10), zoom: 13.5, pitch: 42, bearing: 30 } },
        { type: 'hud-metric', duration: 0, params: { id: 'distance', kind: 'counter', label: 'Route Distance', value: 8100, unit: 'm', severity: 'success' } },
        hudEvent('confirmed', 'ROUTE SELECTED', 'AI-optimised route confirmed. Tanker dispatching on green path.', 'success'),
      ]),

      removePanel('route-compare'),

      productScreen('Tankering Dispatch — Route AI', {
        component: 'placeholder',
        device: 'desktop',
        subtitle: 'Placeholder — tankering dispatch with AI route comparison TBD',
        role: 'Dispatch Controller',
        url: 'app.skewb.io/tankering/dispatch',
      }),
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // ACT 3: TANKER EN ROUTE — Vehicle follows the green path (60–100s)
    // Tanker dispatched along the AI route. Carbon ticks up (but less).
    // CSO level dropping as tanker approaches. Route line stays visible.
    // ═══════════════════════════════════════════════════════════════════════
    act('Tanker Dispatch', [
      spawnVehicle('tanker-1', 'simpoly-tanker', DEPOT, CSO, 'Tanker 01', 14000),

      beat(7000, [
        { type: 'camera', duration: 7000, params: { center: gp(-16, -4), zoom: 14.5, pitch: 45, bearing: -10 } },
        hudEvent('dispatch', 'TANKER EN ROUTE', 'Tanker 01 dispatched on AI-optimised route. ETA: 14 minutes.', 'info'),
        { type: 'hud-metric', duration: 0, params: { id: 'carbon', kind: 'counter', label: 'Carbon', value: 4, unit: 'kg CO₂', severity: 'info' } },
        { type: 'hud-metric', duration: 0, params: { id: 'cso-level', kind: 'counter', label: 'CSO Level', value: 82, unit: '%', severity: 'critical' } },
        hudTeam([
          { name: 'Tanker 01', status: 'en-route', destination: 'CSO River Point' },
          { name: 'Tanker 02', status: 'standby', destination: 'Depot West' },
        ]),
      ]),

      beat(7000, [
        orbit180(CSO, { startBearing: -10, arc: 80, zoom: 14.5, pitch: 42, duration: 7000 }),
        { type: 'hud-metric', duration: 0, params: { id: 'carbon', kind: 'counter', label: 'Carbon', value: 8, unit: 'kg CO₂', severity: 'info' } },
        { type: 'hud-metric', duration: 0, params: { id: 'cso-level', kind: 'counter', label: 'CSO Level', value: 72, unit: '%', severity: 'warning' } },
        hudEvent('progress', 'CSO LEVEL DROPPING', 'Tanker approaching. CSO reducing via natural drainage + tanker suction.', 'info'),
      ]),

      // Tanker arrives — CSO level drops significantly
      beat(PACING.BEAT, [
        dronePushIn(CSO, { bearing: 20, zoom: 15.5 }),
        hudEvent('arrive', 'TANKER ON SITE', 'Tanker 01 arrived at CSO. Suction active. Level dropping rapidly.', 'success'),
        { type: 'hud-metric', duration: 0, params: { id: 'cso-level', kind: 'counter', label: 'CSO Level', value: 54, unit: '%', severity: 'success' } },
        { type: 'hud-metric', duration: 0, params: { id: 'carbon', kind: 'counter', label: 'Carbon', value: 12, unit: 'kg CO₂', severity: 'success' } },
        notification('CSO BELOW THRESHOLD', 'Level at 54% — below 60% target. Overflow averted.', 'success', PACING.BEAT),
      ]),

      // Clear geyser
      { type: 'clear-effect', duration: 0, params: { id: 'cso-overflow' } } as ScenarioStep,
      { type: 'model-status', duration: 0, params: { coordinates: gp(4, -24), infraId: 'cso-river', color: '#10b981', emissive: 0.4 } } as ScenarioStep,

      productScreen('Waste Classification — Tanker Load', {
        component: 'placeholder',
        device: 'desktop',
        subtitle: 'Placeholder — waste classification and tanker load data TBD',
        role: 'Environmental Officer',
        url: 'app.skewb.io/tankering/classification',
      }),
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // ACT 4: RETURN + CARBON SUMMARY — Green resolution (100–140s)
    // Tanker returns to treatment via return route. Colour grade greens.
    // Carbon summary shows total saved vs old route.
    // ═══════════════════════════════════════════════════════════════════════
    act('Carbon Summary', [
      { type: 'clear-effect', duration: 0, params: { id: 'new-route' } } as ScenarioStep,
      { type: 'color-grade', duration: 0, params: { tint: '#10b981', intensity: 0.06, vignette: 0.1 } } as ScenarioStep,

      // Return route flow
      { type: 'flow-pulse', duration: 0, params: {
        id: 'return-route', path: RETURN_ROUTE, color: '#10b981', speed: 1.0,
      }} as ScenarioStep,

      spawnVehicle('tanker-return', 'simpoly-tanker', CSO, TREATMENT, 'Tanker 01', 10000),

      beat(8000, [
        craneReveal(at(-10, -22), { zoom: 14, bearing: -10, duration: 8000 }),
        hudEvent('return', 'TANKER RETURNING', 'Tanker 01 returning to treatment works via optimised return path.', 'info'),
        { type: 'hud-metric', duration: 0, params: { id: 'carbon', kind: 'counter', label: 'Carbon Total', value: 12, unit: 'kg CO₂', severity: 'success' } },
      ]),

      { type: 'clear-effect', duration: 0, params: { id: 'return-route' } } as ScenarioStep,

      dimEnd,

      beat(PACING.BEAT, [
        { type: 'camera', duration: PACING.BEAT, params: { center: gp(-22, -22), infraId: 'water-treatment', zoom: 15, pitch: 48, bearing: 0 } },
        { type: 'model-status', duration: 0, params: { coordinates: gp(-22, -22), infraId: 'water-treatment', color: '#10b981', emissive: 0.5 } },
        hudEvent('treated', 'LOAD DISCHARGED', 'Waste discharged at treatment works. Classification: standard domestic.', 'success'),
      ]),

      // Carbon summary panel
      beat(PACING.BEAT, [
        craneReveal(at(-10, -10), { zoom: 13.8, bearing: 0 }),
        panel('carbon-summary', 'Carbon Summary — This Trip', 'data', TREATMENT, [
          { label: 'Route Used', value: 'AI-Optimised (8.1km)', status: 'good' },
          { label: 'Carbon Emitted', value: '12.1kg CO₂', status: 'good' },
          { label: 'Carbon Saved', value: '6.5kg CO₂ (35%)', status: 'good' },
          { label: 'Annual Projection', value: '2.4 tonnes saved', status: 'good' },
        ]),
        notification('NET-ZERO PROGRESS', '35% carbon reduction this trip. 2.4T annual saving at current rate.', 'success', 5000),
        { type: 'hud-metric', duration: 0, params: { id: 'cso-level', kind: 'counter', label: 'CSO Level', value: 42, unit: '%', severity: 'success' } },
      ]),

      removePanel('carbon-summary'),

      productScreen('AI Reporting — Carbon Dashboard', {
        component: 'placeholder',
        device: 'desktop',
        subtitle: 'Placeholder — carbon dashboard with route optimisation savings TBD',
        role: 'Sustainability Manager',
        url: 'app.skewb.io/energy/carbon-dashboard',
      }),

      { type: 'clear-effect', duration: 0, params: { id: 'carbon' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'cso-level' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'distance' } } as ScenarioStep,

      { type: 'color-grade', duration: 0, params: { tint: '#fbbf24', intensity: 0.04, vignette: 0.1 } } as ScenarioStep,

      beat(PACING.PULLBACK, [
        majesticPullback({ bearing: -20 }),
        notification('NET-ZERO OPS', 'CSO resolved — AI route — 35% carbon saved — 2.4T annual projection.', 'success'),
      ]),

      { type: 'color-grade', duration: 0, params: { tint: 'transparent', intensity: 0, vignette: 0 } } as ScenarioStep,
      hold(PACING.HOLD),
    ]),
  ],
);
